import React from 'react';
import { messagingApi } from '@shared/api/messaging';
import { logger } from '@shared/lib/logger';
import { getCallDeviceId } from './deviceId';

type TelnyxClient = {
  remoteElement?: string | HTMLElement;
  connect: () => void;
  disconnect?: () => void;
  on: (event: string, callback: (payload?: unknown) => void) => TelnyxClient;
  newCall: (options: Record<string, unknown>) => TelnyxCall;
};

type TelnyxCall = {
  id?: string;
  state?: string;
  remoteStream?: MediaStream | null;
  telnyxIDs?: {
    telnyxCallControlId?: string | null;
    telnyxSessionId?: string | null;
    telnyxLegId?: string | null;
    callControlId?: string | null;
    callSessionId?: string | null;
    callLegId?: string | null;
  };
  answer?: () => Promise<void>;
  hangup?: () => Promise<void>;
  muteAudio?: () => Promise<void>;
  unmuteAudio?: () => Promise<void>;
  toggleAudioMute?: () => Promise<void>;
  hold?: () => Promise<void>;
  unhold?: () => Promise<void>;
  resume?: () => Promise<void>;
  setAudioInDevice?: (deviceId: string) => Promise<unknown>;
  setAudioOutDevice?: (deviceId: string) => Promise<unknown>;
};

type AudioDevice = {
  deviceId: string;
  label: string;
  kind: MediaDeviceKind;
};

type ClientStatus = 'unregistered' | 'connecting' | 'ready' | 'error';
type CallStatus = 'idle' | 'requesting' | 'ringing' | 'active' | 'held' | 'hangup' | 'error';
type PermissionState = 'prompt' | 'granted' | 'blocked';

export type BrowserCallLine = {
  id: string;
  sdkCallId: string | null;
  logicalCallId: string | null;
  status: CallStatus;
  isLive: boolean;
  isMuted: boolean;
};

/**
 * One audible route per non-terminal line. The registry component renders a
 * dedicated `<audio>` element per route and keeps only the live, non-held line
 * audible so simultaneous calls never compete for a single shared element.
 */
type CallAudioRoute = {
  id: string;
  stream: MediaStream | null;
  isLive: boolean;
  status: CallStatus;
};

type TelnyxCallIds = {
  telnyxCallId: string | null;
  telnyxCallControlId: string | null;
  telnyxCallSessionId: string | null;
  telnyxCallLegId: string | null;
};

type TelnyxSdkErrorDetails = {
  message: string;
  code: string | number | null;
  name: string | null;
  callId: string | null;
  sessionId: string | null;
  recoverable: boolean | null;
};

export type CallWebRtcEndedDetail = {
  logicalCallId: string | null;
  sdkCallId: string | null;
  lineId: string | null;
};

type State = {
  clientStatus: ClientStatus;
  callStatus: CallStatus;
  isMuted: boolean;
  audioBlocked: boolean;
  activeCall: TelnyxCall | null;
  callLines: BrowserCallLine[];
  audioRoutes: CallAudioRoute[];
  microphones: AudioDevice[];
  speakers: AudioDevice[];
  selectedMicrophoneId: string | null;
  selectedSpeakerId: string | null;
  permissionState: PermissionState;
  errorMessage: string | null;
};

export const callWebRtcEndedEventName = 'jema:calls-webrtc-ended';
export const callWebRtcIncomingEventName = 'jema:calls-webrtc-incoming';
const preferredMicKey = 'jema.calls.preferred-microphone';
const preferredSpeakerKey = 'jema.calls.preferred-speaker';
const normalizeSdkState = (nextState?: string) => nextState?.toLowerCase();
const terminalStates = new Set([
  'bye',
  'closed',
  'destroy',
  'done',
  'ended',
  'failed',
  'hangup',
  'purge',
  'terminated',
]);

let client: TelnyxClient | null = null;
let connecting: Promise<TelnyxClient> | null = null;
// TEMP DIAGNOSTIC (calls_webrtc_invite_trace): a per-client id so multiple tabs
// / devices registered on the same staff WebRTC credential are distinguishable
// in aggregated backend logs. Remove once invite-routing is confirmed.
let clientInstanceId: string | null = null;
const newClientInstanceId = () => {
  try {
    return globalThis.crypto?.randomUUID?.() ?? `client-${Date.now()}-${Math.random()}`;
  } catch {
    return `client-${Date.now()}-${Math.random()}`;
  }
};
let activeCall: TelnyxCall | null = null;
let callerNumber: string | null = null;
let generatedCallLineId = 0;
let liveCallLineId: string | null = null;
// Registration readiness: the SDK reports `telnyx.ready` only once the client
// is actually registered and able to receive inbound invites. `ensureClient`
// resolves as soon as `connect()` is called, which is BEFORE registration — so
// gate the answer/dial path on this promise to avoid dialing a credential that
// has no live registration yet (Telnyx returns SIP 486 in that race).
let clientReady: Promise<void> | null = null;
let resolveClientReady: (() => void) | null = null;
let rejectClientReady: ((error: Error) => void) | null = null;
const resetClientReady = () => {
  clientReady = new Promise<void>((resolve, reject) => {
    resolveClientReady = resolve;
    rejectClientReady = reject;
  });
  // Avoid unhandled-rejection noise when nobody is awaiting registration yet.
  clientReady.catch(() => undefined);
};
const markClientRegistered = () => {
  resolveClientReady?.();
};
const markClientRegistrationFailed = (error: Error) => {
  rejectClientReady?.(error);
};
const teardownClient = (targetClient: TelnyxClient | null = client) => {
  const isCurrentClient = !targetClient || client === targetClient;
  try {
    targetClient?.disconnect?.();
  } catch {
    // Best effort: a half-dead SDK socket may throw while disconnecting.
  }
  if (!isCurrentClient) return;
  client = null;
  connecting = null;
  clientInstanceId = null;
  clientReady = null;
  resolveClientReady = null;
  rejectClientReady = null;
};

if (typeof window !== 'undefined') {
  // A tab that closes, refreshes, or navigates away ungracefully (crash,
  // sleep/wake, network blip) never tells Telnyx to release this SIP
  // credential's registration. Telnyx then keeps it "live" for a while, and
  // the next session reconnects with the same sessionStorage-backed device id
  // (see getCallDeviceId) — same credential — so it collides with its own
  // stale registration and inbound dials bounce as SIP 486 "busy" until the
  // stale registration times out on Telnyx's side. `pagehide` fires on real
  // tab-close/navigation (unlike `visibilitychange`, which also fires on a
  // mere tab switch) so disconnecting here releases the registration
  // immediately instead of waiting on Telnyx's own timeout.
  window.addEventListener('pagehide', () => {
    if (client) teardownClient();
  });
}
const callLineIds = new WeakMap<TelnyxCall, string>();
const callLines = new Map<
  string,
  {
    id: string;
    call: TelnyxCall;
    logicalCallId: string | null;
    status: CallStatus;
    isMuted: boolean;
  }
>();
const incomingCallWaiters = new Set<(call: TelnyxCall) => void>();
let reservedIncomingCalls = new WeakSet<TelnyxCall>();
const outboundCallControlIdWaiters = new Set<{
  lineId: string;
  resolve: (call: TelnyxCall) => void;
  timeout: ReturnType<typeof setTimeout>;
}>();
let forcedInviteReregister: Promise<void> | null = null;
const remoteAudioElements = new Map<string, HTMLAudioElement>();
const sdkRemoteAudioElementId = 'jema-telnyx-sdk-remote-audio';
// Track per-call so device binding and media diagnostics run exactly once, when
// the call first becomes active (its peer connection exists).
const devicePrefsAppliedCalls = new WeakSet<TelnyxCall>();
const mediaDiagnosticsStartedCalls = new WeakSet<TelnyxCall>();

const initialState: State = {
  clientStatus: 'unregistered',
  callStatus: 'idle',
  isMuted: false,
  audioBlocked: false,
  activeCall: null,
  callLines: [],
  audioRoutes: [],
  microphones: [],
  speakers: [],
  selectedMicrophoneId: null,
  selectedSpeakerId: null,
  permissionState: 'prompt',
  errorMessage: null,
};

let state: State = { ...initialState };

const listeners = new Set<() => void>();

const emit = (patch: Partial<State>) => {
  state = { ...state, ...patch };
  listeners.forEach(listener => listener());
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

const getSnapshot = () => state;

const dispatchCallEnded = (detail: CallWebRtcEndedDetail) => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(
    new CustomEvent<CallWebRtcEndedDetail>(callWebRtcEndedEventName, { detail })
  );
};

const getCallLineId = (call: TelnyxCall) => {
  const existing = callLineIds.get(call);
  if (existing) return existing;
  const nextId = call.id ?? `local-call-${++generatedCallLineId}`;
  callLineIds.set(call, nextId);
  return nextId;
};

const serializeCallLines = (): BrowserCallLine[] =>
  [...callLines.values()].map(line => ({
    id: line.id,
    sdkCallId: line.call.id ?? null,
    logicalCallId: line.logicalCallId,
    status: line.status,
    isLive: line.id === liveCallLineId,
    isMuted: line.isMuted,
  }));

const serializeAudioRoutes = (): CallAudioRoute[] =>
  [...callLines.values()]
    .filter(
      line =>
        !terminalStates.has(normalizeSdkState(line.call.state) ?? '') && line.status !== 'hangup'
    )
    .map(line => ({
      id: line.id,
      stream: line.call.remoteStream ?? null,
      isLive: line.id === liveCallLineId,
      status: line.status,
    }));

const findCallLine = (lineId?: string | null) => {
  if (lineId) {
    const exact = callLines.get(lineId);
    if (exact) return exact;
    const logical = [...callLines.values()].find(line => line.logicalCallId === lineId);
    if (logical) return logical;
  }
  return null;
};

const syncCallLines = () => {
  const liveLine = liveCallLineId ? callLines.get(liveCallLineId) : null;
  const fallbackLine = [...callLines.values()].find(
    line => !terminalStates.has(normalizeSdkState(line.call.state) ?? '')
  );
  activeCall = liveLine?.call ?? fallbackLine?.call ?? null;
  emit({
    activeCall,
    callStatus: liveLine?.status ?? fallbackLine?.status ?? 'idle',
    callLines: serializeCallLines(),
    audioRoutes: serializeAudioRoutes(),
  });
};

const markAudioPlaybackBlocked = (lineId: string, error: unknown) => {
  logger.warn('Telnyx WebRTC remote audio playback was blocked', {
    lineId,
    error: error instanceof Error ? error.message : String(error),
    category: 'calls_webrtc_sdk',
  });
  emit({ audioBlocked: true });
};

const ensureSdkRemoteAudioElement = () => {
  if (typeof document === 'undefined') return null;
  const existing = document.getElementById(sdkRemoteAudioElementId);
  if (existing instanceof HTMLAudioElement) return existing;

  const audio = document.createElement('audio');
  audio.id = sdkRemoteAudioElementId;
  audio.autoplay = true;
  // The SDK requires a remote element, while Jema renders one controlled
  // element per logical line below. Keep this transport element silent so the
  // same remote stream is never played twice.
  audio.muted = true;
  audio.volume = 0;
  audio.setAttribute('playsinline', 'true');
  audio.className = 'hidden';
  audio.dataset.telnyxSdkRemoteAudio = 'true';
  document.body.appendChild(audio);
  return audio;
};

const mapCallState = (nextState?: string): CallStatus => {
  const sdkState = normalizeSdkState(nextState);
  if (!sdkState) return 'idle';
  if (sdkState === 'requesting' || sdkState === 'trying') return 'requesting';
  if (sdkState === 'ringing' || sdkState === 'answering' || sdkState === 'early') {
    return 'ringing';
  }
  if (sdkState === 'active' || sdkState === 'answered' || sdkState === 'connected') {
    return 'active';
  }
  if (sdkState === 'held') return 'held';
  if (terminalStates.has(sdkState)) return 'hangup';
  return 'idle';
};

const resolveDeviceId = (devices: AudioDevice[], preferredDeviceId: string | null) => {
  if (preferredDeviceId && devices.some(device => device.deviceId === preferredDeviceId)) {
    return preferredDeviceId;
  }
  return (
    devices.find(device => device.deviceId === 'default')?.deviceId ?? devices[0]?.deviceId ?? null
  );
};

const resolveSelectedDeviceId = (devices: AudioDevice[], requestedDeviceId: string) => {
  if (requestedDeviceId && devices.some(device => device.deviceId === requestedDeviceId)) {
    return requestedDeviceId;
  }
  return resolveDeviceId(devices, null);
};

const updateDevices = async () => {
  if (typeof navigator === 'undefined' || !navigator.mediaDevices?.enumerateDevices) return;
  const devices = await navigator.mediaDevices.enumerateDevices();
  const microphones = devices
    .filter(device => device.kind === 'audioinput')
    .map((device, index) => ({
      deviceId: device.deviceId,
      kind: device.kind,
      label: device.label || `Microphone ${index + 1}`,
    }));
  const speakers = devices
    .filter(device => device.kind === 'audiooutput')
    .map((device, index) => ({
      deviceId: device.deviceId,
      kind: device.kind,
      label: device.label || `Speaker ${index + 1}`,
    }));
  const preferredMic =
    typeof window === 'undefined' ? null : window.localStorage.getItem(preferredMicKey);
  const preferredSpeaker =
    typeof window === 'undefined' ? null : window.localStorage.getItem(preferredSpeakerKey);
  emit({
    microphones,
    speakers,
    selectedMicrophoneId: resolveDeviceId(microphones, preferredMic),
    selectedSpeakerId: resolveDeviceId(speakers, preferredSpeaker),
  });
};

const requestMicrophonePermission = async () => {
  if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
    emit({ permissionState: 'blocked' });
    throw new Error('Microphone access is not supported in this browser.');
  }
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    stream.getTracks().forEach(track => track.stop());
    emit({ permissionState: 'granted' });
    await updateDevices();
  } catch {
    emit({ permissionState: 'blocked' });
    throw new Error('Microphone permission is blocked.');
  }
};

const applyCallDevicePreference = async (
  call: TelnyxCall,
  deviceKind: 'audioinput' | 'audiooutput',
  apply: () => Promise<unknown>
) => {
  try {
    await apply();
  } catch (error) {
    logger.warn('Telnyx WebRTC device preference could not be applied yet', {
      deviceKind,
      callId: call.id ?? null,
      callState: call.state ?? null,
      error: error instanceof Error ? error.message : String(error),
      category: 'calls_webrtc_sdk',
    });
  }
};

const applyStoredDevicePreferences = async (call: TelnyxCall) => {
  // The SDK can only bind input/output devices once the call has a live peer
  // connection. Calling setAudioIn/OutDevice at answer/dial time throws
  // "Cannot read properties of undefined (reading 'options')" inside the SDK and
  // leaves the OUTPUT device unbound — so the agent hears nothing from the remote
  // party. Apply only once the call is active; handleCallUpdate re-invokes this on
  // the 'active' transition.
  if (mapCallState(call.state) !== 'active') return;
  const mic = state.selectedMicrophoneId;
  const speaker = state.selectedSpeakerId;
  if (mic && call.setAudioInDevice) {
    const setAudioInDevice = call.setAudioInDevice;
    await applyCallDevicePreference(call, 'audioinput', () => setAudioInDevice(mic));
  }
  if (speaker && call.setAudioOutDevice) {
    const setAudioOutDevice = call.setAudioOutDevice;
    await applyCallDevicePreference(call, 'audiooutput', () => setAudioOutDevice(speaker));
  }
};

const readTelnyxIds = (call: TelnyxCall) => ({
  telnyxCallId: call.id ?? null,
  telnyxCallControlId: call.telnyxIDs?.telnyxCallControlId ?? call.telnyxIDs?.callControlId ?? null,
  telnyxCallSessionId: call.telnyxIDs?.telnyxSessionId ?? call.telnyxIDs?.callSessionId ?? null,
  telnyxCallLegId: call.telnyxIDs?.telnyxLegId ?? call.telnyxIDs?.callLegId ?? null,
});

const waitForOutboundCallControlId = async (
  call: TelnyxCall,
  { timeoutMs = 1500 }: { timeoutMs?: number } = {}
): Promise<TelnyxCallIds> => {
  const immediateIds = readTelnyxIds(call);
  if (immediateIds.telnyxCallControlId) return immediateIds;

  const lineId = getCallLineId(call);
  return new Promise(resolve => {
    const timeout = setTimeout(() => {
      outboundCallControlIdWaiters.delete(waiter);
      const ids = readTelnyxIds(call);
      logger.warn('Telnyx WebRTC call-control id was not available before call recording', {
        sdkCallId: ids.telnyxCallId,
        lineId,
        category: 'calls_webrtc_sdk',
      });
      resolve(ids);
    }, timeoutMs);
    const waiter = {
      lineId,
      timeout,
      resolve: (updatedCall: TelnyxCall) => resolve(readTelnyxIds(updatedCall)),
    };
    outboundCallControlIdWaiters.add(waiter);
  });
};

const isAnswerableIncomingCall = (call: TelnyxCall | null) => {
  const callStatus = mapCallState(call?.state);
  return Boolean(call?.answer && (callStatus === 'ringing' || callStatus === 'requesting'));
};

const findAnswerableIncomingCall = () => {
  if (isAnswerableIncomingCall(activeCall)) return activeCall;
  return [...callLines.values()].find(line => isAnswerableIncomingCall(line.call))?.call ?? null;
};

const findUnreservedAnswerableIncomingCall = () => {
  const candidates = [activeCall, ...[...callLines.values()].map(line => line.call)];
  return (
    candidates.find((call): call is TelnyxCall =>
      Boolean(call && !reservedIncomingCalls.has(call) && isAnswerableIncomingCall(call))
    ) ?? null
  );
};

const describeActiveCall = (call: TelnyxCall | null) => {
  if (!call) return 'none';
  const stateLabel = call.state ?? 'unknown';
  return call.id ? `${stateLabel} (${call.id})` : stateLabel;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const stringValue = (value: unknown) => {
  if (typeof value === 'string' && value.trim()) return value;
  if (typeof value === 'number') return String(value);
  return null;
};

const sdkCodeValue = (value: unknown) => {
  if (typeof value === 'number') return value;
  return stringValue(value);
};

const normalizeTelnyxSdkError = (payload: unknown): TelnyxSdkErrorDetails => {
  if (payload instanceof Error) {
    return {
      message: payload.message || 'Telnyx WebRTC connection failed.',
      code: null,
      name: payload.name || null,
      callId: null,
      sessionId: null,
      recoverable: null,
    };
  }

  const payloadRecord = isRecord(payload) ? payload : null;
  const nestedError = payloadRecord && isRecord(payloadRecord.error) ? payloadRecord.error : null;
  const errorRecord = nestedError ?? payloadRecord;
  const message =
    stringValue(errorRecord?.message) ??
    stringValue(payloadRecord?.message) ??
    stringValue(payloadRecord?.errorMessage) ??
    'Telnyx WebRTC connection failed.';

  return {
    message,
    code: sdkCodeValue(errorRecord?.code) ?? sdkCodeValue(payloadRecord?.code),
    name: stringValue(errorRecord?.name) ?? stringValue(payloadRecord?.name),
    callId:
      stringValue(payloadRecord?.callId) ??
      stringValue(payloadRecord?.callID) ??
      stringValue(payloadRecord?.call_id),
    sessionId: stringValue(payloadRecord?.sessionId) ?? stringValue(payloadRecord?.session_id),
    recoverable: typeof payloadRecord?.recoverable === 'boolean' ? payloadRecord.recoverable : null,
  };
};

const hasKnownCallScopedError = (details: TelnyxSdkErrorDetails) => {
  if (!details.callId) return false;
  if (activeCall?.id === details.callId) return true;
  return [...callLines.values()].some(
    line => line.id === details.callId || line.call.id === details.callId
  );
};

const isCurrentCallScopedError = (details: TelnyxSdkErrorDetails) => {
  if (!details.callId) return false;
  if (activeCall?.id === details.callId) return true;
  const liveLine = liveCallLineId ? callLines.get(liveCallLineId) : null;
  return liveLine?.call.id === details.callId || liveLine?.id === details.callId;
};

const inFlightCallLines = () =>
  [...callLines.values()].filter(
    line => !terminalStates.has(normalizeSdkState(line.call.state) ?? '')
  );

const isStalePeerSetupFailure = (details: TelnyxSdkErrorDetails) => {
  const message = details.message.toLowerCase();
  const code = details.code !== null ? String(details.code).toLowerCase() : '';
  const name = details.name?.toLowerCase() ?? '';
  return (
    message.includes('telnyx webrtc connection failed') ||
    message.includes('call was closed during setup') ||
    message.includes('failed to hang up cleanly') ||
    code.includes('peer_closed_during_init') ||
    name.includes('peer_closed_during_init') ||
    // BYE_SEND_FAILED (44003): the SDK could not send a BYE because the peer/socket
    // for that call is already gone. It is always post-call cleanup — never a reason
    // to tear down the registered client (doing so unregisters the device and makes
    // it deaf to the next inbound invite until it re-registers).
    code === '44003' ||
    name.includes('bye_send_failed')
  );
};

const isUntrackedStaleCallCleanupFailure = (
  details: TelnyxSdkErrorDetails,
  isKnownCallError: boolean
) => Boolean(details.callId && !isKnownCallError && isStalePeerSetupFailure(details));

const isNonCurrentStaleCallCleanupFailure = (details: TelnyxSdkErrorDetails) =>
  Boolean(details.callId && !isCurrentCallScopedError(details) && isStalePeerSetupFailure(details));

const createIncomingCallUnavailableError = (timing: string) => {
  const lastError = state.errorMessage ? ` Last SDK error: ${state.errorMessage}.` : '';
  const error = new Error(
    `No Telnyx WebRTC invite reached this browser ${timing}. ` +
      `SDK client: ${state.clientStatus}; SDK call: ${state.callStatus}; ` +
      `active SDK call: ${describeActiveCall(activeCall)}.${lastError}`
  );
  logger.error('Telnyx inbound WebRTC invite unavailable', error, {
    timing,
    clientInstanceId,
    clientStatus: state.clientStatus,
    callStatus: state.callStatus,
    permissionState: state.permissionState,
    activeCall: describeActiveCall(activeCall),
    hasClient: Boolean(client),
    waiterCount: incomingCallWaiters.size,
    lastSdkError: state.errorMessage,
    category: 'calls_webrtc_sdk',
  });
  return error;
};

const resolveIncomingCallWaiters = (call: TelnyxCall) => {
  if (!isAnswerableIncomingCall(call) || reservedIncomingCalls.has(call)) return;
  const nextWaiter = incomingCallWaiters.values().next().value;
  if (nextWaiter) {
    reservedIncomingCalls.add(call);
    nextWaiter(call);
  }
};

const resolveOutboundCallControlIdWaiters = (call: TelnyxCall) => {
  if (!readTelnyxIds(call).telnyxCallControlId) return;
  const lineId = getCallLineId(call);
  outboundCallControlIdWaiters.forEach(waiter => {
    if (waiter.lineId !== lineId) return;
    clearTimeout(waiter.timeout);
    outboundCallControlIdWaiters.delete(waiter);
    waiter.resolve(call);
  });
};

const waitForIncomingCallUpdate = (timeoutMs: number) =>
  new Promise<TelnyxCall | null>(resolve => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    const cleanup = () => {
      incomingCallWaiters.delete(handleReady);
      if (timeoutId) clearTimeout(timeoutId);
    };
    const handleReady = (call: TelnyxCall) => {
      cleanup();
      resolve(call);
    };

    incomingCallWaiters.add(handleReady);
    timeoutId = setTimeout(() => {
      cleanup();
      resolve(null);
    }, timeoutMs);
  });

const forceClientReregisterBeforeInviteTimeout = async ({ timeoutMs }: { timeoutMs: number }) => {
  const targetClient = client;
  if (!targetClient || state.clientStatus !== 'ready' || inFlightCallLines().length > 0) return;
  if (!forcedInviteReregister) {
    forcedInviteReregister = (async () => {
      logger.warn('Telnyx WebRTC forcing re-register before inbound invite timeout', {
        clientInstanceId,
        timeoutMs,
        category: 'calls_webrtc_invite_trace',
      });
      try {
        targetClient.disconnect?.();
      } catch {
        // Best effort: a half-dead SDK socket may throw while disconnecting.
      }
      resetClientReady();
      emit({ clientStatus: 'connecting', errorMessage: null });
      try {
        targetClient.connect();
      } catch (error) {
        const normalized =
          error instanceof Error ? error : new Error('Telnyx WebRTC connection failed.');
        markClientRegistrationFailed(normalized);
        emit({ clientStatus: 'error', callStatus: 'error', errorMessage: normalized.message });
        teardownClient(targetClient);
        throw normalized;
      }
      await waitForClientRegistered({ timeoutMs });
    })().finally(() => {
      forcedInviteReregister = null;
    });
  }
  await forcedInviteReregister;
};

const rememberCallLine = (
  call: TelnyxCall,
  {
    logicalCallId,
    preferLive = false,
    status = mapCallState(call.state),
  }: { logicalCallId?: string | null; preferLive?: boolean; status?: CallStatus } = {}
) => {
  const id = getCallLineId(call);
  const existing = callLines.get(id);
  callLines.set(id, {
    id,
    call,
    logicalCallId: logicalCallId ?? existing?.logicalCallId ?? null,
    status,
    isMuted: existing?.isMuted ?? false,
  });
  if (preferLive || status === 'active') {
    liveCallLineId = id;
  }
  if (status === 'held' && liveCallLineId === id) {
    liveCallLineId = null;
  }
  if (status === 'hangup') {
    callLines.delete(id);
    if (liveCallLineId === id) {
      liveCallLineId = null;
    }
  }
  syncCallLines();
  return callLines.get(id) ?? null;
};

// Read-only media diagnostics: once a call is active, sample the underlying
// RTCPeerConnection twice to record whether inbound RTP is actually arriving and
// which ICE candidate types won. This distinguishes "output device not bound"
// (inboundAudioPackets > 0 but agent can't hear) from "media path broken / TURN"
// (inboundAudioPackets stuck at 0). Remove once calling audio is confirmed stable.
type PeerStatLike = Record<string, unknown> & { type?: string };
const logCallMediaDiagnostics = (call: TelnyxCall) => {
  const pc =
    (call as unknown as { peer?: { instance?: RTCPeerConnection | null } | null }).peer?.instance ??
    null;
  if (!pc) {
    logger.warn('Telnyx WebRTC media diagnostic: no peer connection', {
      sdkCallId: call.id ?? null,
      category: 'calls_webrtc_media_diag',
    });
    return;
  }
  const sample = async (label: string) => {
    try {
      const stats = await pc.getStats();
      let inboundAudioPackets: number | null = null;
      let inboundAudioBytes: number | null = null;
      let outboundAudioPackets: number | null = null;
      let selectedLocalType: string | null = null;
      let selectedRemoteType: string | null = null;
      const localCandidateTypes = new Set<string>();
      stats.forEach(report => {
        const r = report as PeerStatLike;
        if (r.type === 'inbound-rtp' && r.kind === 'audio') {
          inboundAudioPackets = (r.packetsReceived as number) ?? null;
          inboundAudioBytes = (r.bytesReceived as number) ?? null;
        }
        if (r.type === 'outbound-rtp' && r.kind === 'audio') {
          outboundAudioPackets = (r.packetsSent as number) ?? null;
        }
        if (r.type === 'local-candidate' && typeof r.candidateType === 'string') {
          localCandidateTypes.add(r.candidateType);
        }
        if (r.type === 'candidate-pair' && r.nominated && r.state === 'succeeded') {
          selectedLocalType =
            ((stats.get(r.localCandidateId as string) as PeerStatLike | undefined)
              ?.candidateType as string) ?? null;
          selectedRemoteType =
            ((stats.get(r.remoteCandidateId as string) as PeerStatLike | undefined)
              ?.candidateType as string) ?? null;
        }
      });
      logger.warn('Telnyx WebRTC media diagnostic', {
        label,
        sdkCallId: call.id ?? null,
        iceConnectionState: pc.iceConnectionState,
        connectionState: pc.connectionState,
        inboundAudioPackets,
        inboundAudioBytes,
        outboundAudioPackets,
        selectedLocalType,
        selectedRemoteType,
        localCandidateTypes: Array.from(localCandidateTypes),
        category: 'calls_webrtc_media_diag',
      });
    } catch (error) {
      logger.warn('Telnyx WebRTC media diagnostic failed', {
        label,
        sdkCallId: call.id ?? null,
        error: error instanceof Error ? error.message : String(error),
        category: 'calls_webrtc_media_diag',
      });
    }
  };
  setTimeout(() => void sample('t+3s'), 3000);
  setTimeout(() => void sample('t+6s'), 6000);
};

const handleCallUpdate = (call: TelnyxCall) => {
  const callStatus = mapCallState(call.state);
  const lineId = getCallLineId(call);
  const logicalCallId = callLines.get(lineId)?.logicalCallId ?? null;
  // TEMP DIAGNOSTIC (calls_webrtc_invite_trace): records every SDK callUpdate so
  // we can confirm whether an inbound invite actually reaches THIS browser/tab
  // (vs being routed to another registration on the shared credential). warn so
  // it ships to the backend /logs sink. Remove once invite-routing is confirmed.
  logger.warn('Telnyx WebRTC callUpdate received', {
    clientInstanceId,
    sdkCallId: call.id ?? null,
    sdkState: call.state ?? null,
    mappedStatus: callStatus,
    isAnswerableIncoming: isAnswerableIncomingCall(call),
    hasAnswerFn: Boolean(call.answer),
    waiterCount: incomingCallWaiters.size,
    category: 'calls_webrtc_invite_trace',
  });
  rememberCallLine(call, { status: callStatus });
  resolveOutboundCallControlIdWaiters(call);
  if (callStatus === 'active') {
    // Bind the audio input/output devices now that the peer exists (earlier
    // attempts at answer/dial time throw inside the SDK), and kick off media
    // diagnostics. Both guarded to run once per call.
    if (!devicePrefsAppliedCalls.has(call)) {
      devicePrefsAppliedCalls.add(call);
      void applyStoredDevicePreferences(call);
    }
    if (!mediaDiagnosticsStartedCalls.has(call)) {
      mediaDiagnosticsStartedCalls.add(call);
      logCallMediaDiagnostics(call);
    }
  }
  if (terminalStates.has(normalizeSdkState(call.state) ?? '')) {
    dispatchCallEnded({ logicalCallId, sdkCallId: call.id ?? null, lineId });
    return;
  }
  if (isAnswerableIncomingCall(call) && typeof window !== 'undefined') {
    // Push signal for the dock: an invite reached this browser, so backend
    // call state changed. Lets the UI refetch immediately instead of relying
    // on a fast poll loop.
    window.dispatchEvent(new CustomEvent(callWebRtcIncomingEventName));
  }
  resolveIncomingCallWaiters(call);
};

const waitForIncomingSdkCall = async ({ timeoutMs = 10000 }: { timeoutMs?: number } = {}) => {
  const answerableCall = findUnreservedAnswerableIncomingCall();
  // TEMP DIAGNOSTIC (calls_webrtc_invite_trace): marks the start of an invite
  // wait so we can correlate it against callUpdate records (or their absence).
  // Remove once invite-routing is confirmed.
  logger.warn('Telnyx WebRTC waiting for inbound invite', {
    clientInstanceId,
    timeoutMs,
    foundAnswerableImmediately: Boolean(answerableCall),
    clientStatus: state.clientStatus,
    knownCallLines: callLines.size,
    category: 'calls_webrtc_invite_trace',
  });
  if (answerableCall) {
    reservedIncomingCalls.add(answerableCall);
    return answerableCall;
  }

  const firstWaitMs = Math.max(1, Math.floor(timeoutMs / 2));
  const secondWaitMs = Math.max(1, timeoutMs - firstWaitMs);
  const firstCall = await waitForIncomingCallUpdate(firstWaitMs);
  if (firstCall) return firstCall;

  await forceClientReregisterBeforeInviteTimeout({ timeoutMs: secondWaitMs });
  const refreshedCall = findUnreservedAnswerableIncomingCall();
  if (refreshedCall) {
    reservedIncomingCalls.add(refreshedCall);
    return refreshedCall;
  }

  const secondCall = await waitForIncomingCallUpdate(secondWaitMs);
  if (secondCall) return secondCall;

  throw createIncomingCallUnavailableError(`within ${timeoutMs}ms`);
};

const ensureClient = async () => {
  if (client) return client;
  if (connecting) return connecting;

  connecting = (async () => {
    emit({ clientStatus: 'connecting', errorMessage: null });
    const deviceId = getCallDeviceId();
    const [tokenResponse, telnyxModule] = await Promise.all([
      messagingApi.createWebRtcToken(deviceId),
      import('@telnyx/webrtc'),
    ]);
    callerNumber = tokenResponse.callerNumber ?? null;
    const TelnyxRTC = (
      telnyxModule as unknown as { TelnyxRTC: new (options: unknown) => TelnyxClient }
    ).TelnyxRTC;
    // ICE policy: keep Telnyx relay/TURN candidates available through the SDK
    // defaults, but never force relay-only. Production diagnostics showed
    // relay-only ICE can leave calls stuck in setup/checking when TURN allocation
    // is unavailable; full gathering lets the browser fall back to host/srflx.
    const nextClient = new TelnyxRTC({
      login_token: tokenResponse.loginToken,
    });
    const remoteElement = ensureSdkRemoteAudioElement();
    if (remoteElement) {
      nextClient.remoteElement = remoteElement;
    }
    clientInstanceId = newClientInstanceId();
    resetClientReady();
    // TEMP DIAGNOSTIC (calls_webrtc_invite_trace): one record per client that
    // registers on this credential, so concurrent tabs/devices are visible in
    // backend logs. Remove once invite-routing is confirmed.
    logger.warn('Telnyx WebRTC client created', {
      clientInstanceId,
      credentialId: tokenResponse.credentialId ?? null,
      category: 'calls_webrtc_invite_trace',
    });
    nextClient
      .on('telnyx.ready', () => {
        logger.warn('Telnyx WebRTC client ready', {
          clientInstanceId,
          category: 'calls_webrtc_invite_trace',
        });
        markClientRegistered();
        emit({ clientStatus: 'ready', errorMessage: null });
      })
      .on('telnyx.error', payload => {
        const telnyxError = normalizeTelnyxSdkError(payload);
        const error = payload instanceof Error ? payload : new Error(telnyxError.message);
        const isKnownCallError = hasKnownCallScopedError(telnyxError);
        if (isNonCurrentStaleCallCleanupFailure(telnyxError)) {
          logger.warn('Telnyx WebRTC stale call cleanup error ignored', {
            clientStatus: state.clientStatus,
            callStatus: state.callStatus,
            activeCall: describeActiveCall(activeCall),
            telnyxCallId: telnyxError.callId,
            telnyxSessionId: telnyxError.sessionId,
            telnyxErrorCode: telnyxError.code,
            telnyxErrorName: telnyxError.name,
            recoverable: telnyxError.recoverable,
            category: 'calls_webrtc_sdk',
          });
          return;
        }
        if (isUntrackedStaleCallCleanupFailure(telnyxError, isKnownCallError)) {
          logger.warn('Telnyx WebRTC stale call cleanup error ignored', {
            clientStatus: state.clientStatus,
            callStatus: state.callStatus,
            activeCall: describeActiveCall(activeCall),
            telnyxCallId: telnyxError.callId,
            telnyxSessionId: telnyxError.sessionId,
            telnyxErrorCode: telnyxError.code,
            telnyxErrorName: telnyxError.name,
            recoverable: telnyxError.recoverable,
            category: 'calls_webrtc_sdk',
          });
          return;
        }
        if (
          state.clientStatus === 'ready' &&
          state.callStatus === 'idle' &&
          !activeCall &&
          !isKnownCallError &&
          inFlightCallLines().length === 0 &&
          isStalePeerSetupFailure(telnyxError)
        ) {
          logger.warn('Telnyx WebRTC peer setup failed after call cleanup', {
            clientStatus: state.clientStatus,
            callStatus: state.callStatus,
            activeCall: describeActiveCall(activeCall),
            telnyxCallId: telnyxError.callId,
            telnyxSessionId: telnyxError.sessionId,
            telnyxErrorCode: telnyxError.code,
            telnyxErrorName: telnyxError.name,
            recoverable: telnyxError.recoverable,
            category: 'calls_webrtc_sdk',
          });
          return;
        }
        logger.error('Telnyx WebRTC client error', error, {
          clientStatus: state.clientStatus,
          callStatus: state.callStatus,
          activeCall: describeActiveCall(activeCall),
          telnyxCallId: telnyxError.callId,
          telnyxSessionId: telnyxError.sessionId,
          telnyxErrorCode: telnyxError.code,
          telnyxErrorName: telnyxError.name,
          recoverable: telnyxError.recoverable,
          isKnownCallError,
          category: 'calls_webrtc_sdk',
        });
        if (isKnownCallError) {
          emit({ callStatus: 'error', errorMessage: telnyxError.message });
          return;
        }
        // A media/ICE failure the SDK could not tag with a callId (e.g. the
        // generic "Telnyx WebRTC connection failed" thrown right after the SDP
        // answer) while a call is in progress: fail just that call and keep the
        // registered client. Tearing the whole client down here would leave the
        // device unregistered — and deaf to inbound invites — until the dock
        // re-registers WEBRTC_RECONNECT_DELAY_MS later, which reads as "had to
        // reload to take the next call".
        if (state.clientStatus === 'ready' && inFlightCallLines().length > 0) {
          emit({ errorMessage: telnyxError.message });
          return;
        }
        markClientRegistrationFailed(error);
        emit({ clientStatus: 'error', callStatus: 'error', errorMessage: telnyxError.message });
        teardownClient(nextClient);
      })
      .on('telnyx.notification', payload => {
        const notification = payload as { type?: string; call?: TelnyxCall };
        if (notification.type === 'callUpdate' && notification.call) {
          handleCallUpdate(notification.call);
        }
      });
    client = nextClient;
    nextClient.connect();
    return nextClient;
  })()
    .catch((error: unknown) => {
      // A failed token fetch (e.g. rate-limited) must not leave the client
      // stuck in 'connecting' forever — surface 'error' so callers can retry
      // on their own (slow) schedule.
      const normalized =
        error instanceof Error ? error : new Error('Telnyx WebRTC connection failed.');
      markClientRegistrationFailed(normalized);
      emit({
        clientStatus: 'error',
        errorMessage: normalized.message,
      });
      throw error;
    })
    .finally(() => {
      connecting = null;
    });

  return connecting;
};

export class CallDeviceNotConnectedError extends Error {
  constructor(message = 'This device is not connected to the calling service.') {
    super(message);
    this.name = 'CallDeviceNotConnectedError';
  }
}

/**
 * Resolves only once the SDK has actually registered (`telnyx.ready`), so the
 * backend never dials/transfers to a credential whose browser socket is not yet
 * registered. Throws `CallDeviceNotConnectedError` if registration does not land
 * in time, giving the UI a precise, actionable failure instead of a later
 * "no invite" timeout.
 */
const waitForClientRegistered = async ({ timeoutMs = 8000 }: { timeoutMs?: number } = {}) => {
  if (state.clientStatus === 'ready') return;
  const readiness = clientReady;
  if (!readiness) {
    throw new CallDeviceNotConnectedError();
  }
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  try {
    await Promise.race([
      readiness,
      new Promise<never>((_, reject) => {
        timeoutId = setTimeout(
          () =>
            reject(
              new CallDeviceNotConnectedError(
                'This device is still connecting to the calling service. Check your network and try again.'
              )
            ),
          timeoutMs
        );
      }),
    ]);
  } catch (error) {
    logger.warn('Telnyx WebRTC client not registered before call action', {
      clientInstanceId,
      clientStatus: state.clientStatus,
      timeoutMs,
      error: error instanceof Error ? error.message : String(error),
      category: 'calls_webrtc_sdk',
    });
    throw error instanceof CallDeviceNotConnectedError ? error : new CallDeviceNotConnectedError();
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }
};

const holdCallAudio = async (call: TelnyxCall) => {
  if (call.hold) {
    await call.hold();
  }
};

const resumeCallAudio = async (call: TelnyxCall) => {
  if (call.unhold) {
    await call.unhold();
    return;
  }
  if (call.resume) {
    await call.resume();
  }
};

const holdLine = async (lineId?: string | null) => {
  const line = findCallLine(lineId ?? liveCallLineId);
  if (!line) return null;
  await holdCallAudio(line.call);
  line.status = 'held';
  if (liveCallLineId === line.id) {
    liveCallLineId = null;
  }
  syncCallLines();
  return line.id;
};

const holdCurrentLiveLine = async (exceptLineId?: string | null) => {
  const liveLine = liveCallLineId ? callLines.get(liveCallLineId) : null;
  if (!liveLine || liveLine.id === exceptLineId || liveLine.logicalCallId === exceptLineId) {
    return null;
  }
  return holdLine(liveLine.id);
};

const resumeLine = async (lineId?: string | null) => {
  const line =
    findCallLine(lineId) ?? [...callLines.values()].find(candidate => candidate.status === 'held');
  if (!line) return null;
  await holdCurrentLiveLine(line.id);
  await resumeCallAudio(line.call);
  line.status = 'active';
  liveCallLineId = line.id;
  await applyStoredDevicePreferences(line.call);
  syncCallLines();
  return line.id;
};

/**
 * Renders one hidden `<audio>` element per non-terminal call line and routes
 * each line's own `remoteStream` to it. Only the live, non-held line is audible
 * (others are muted + paused) so simultaneous calls never collide, and the
 * selected speaker sink is applied per element.
 */
export const TelnyxRemoteAudio = () => {
  const snapshot = React.useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  return (
    <>
      {snapshot.audioRoutes.map(route => (
        <RemoteAudioLine key={route.id} route={route} speakerId={snapshot.selectedSpeakerId} />
      ))}
    </>
  );
};

function RemoteAudioLine({
  route,
  speakerId,
}: {
  route: CallAudioRoute;
  speakerId: string | null;
}) {
  const audioRef = React.useRef<HTMLAudioElement | null>(null);
  const audible = route.isLive && route.status !== 'held';

  React.useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return undefined;
    remoteAudioElements.set(route.id, audio);
    return () => {
      remoteAudioElements.delete(route.id);
    };
  }, [route.id]);

  React.useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (route.stream) {
      if (audio.srcObject !== route.stream) {
        audio.srcObject = route.stream;
      }
    } else if (audio.srcObject) {
      audio.srcObject = null;
    }
  }, [route.stream]);

  React.useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.muted = !audible;
    if (audible) {
      void audio.play?.()?.catch(error => markAudioPlaybackBlocked(route.id, error));
    } else {
      audio.pause?.();
    }
  }, [audible, route.id, route.stream]);

  React.useEffect(() => {
    const audio = audioRef.current as
      | (HTMLAudioElement & { setSinkId?: (sinkId: string) => Promise<void> })
      | null;
    if (audio?.setSinkId) {
      void audio.setSinkId(speakerId ?? '').catch(() => undefined);
    }
  }, [speakerId]);

  return <audio ref={audioRef} autoPlay playsInline className='hidden' data-call-line={route.id} />;
}

export const resetTelnyxWebRtcClientForTests = () => {
  teardownClient();
  activeCall = null;
  callerNumber = null;
  generatedCallLineId = 0;
  liveCallLineId = null;
  callLines.clear();
  remoteAudioElements.clear();
  incomingCallWaiters.clear();
  reservedIncomingCalls = new WeakSet<TelnyxCall>();
  outboundCallControlIdWaiters.forEach(waiter => clearTimeout(waiter.timeout));
  outboundCallControlIdWaiters.clear();
  forcedInviteReregister = null;
  state = { ...initialState };
  listeners.clear();
};

const retryRemoteAudioPlayback = async () => {
  const routes = state.audioRoutes.filter(route => route.isLive && route.status !== 'held');
  const playableRoutes = routes
    .map(route => ({ route, audio: remoteAudioElements.get(route.id) }))
    .filter((item): item is { route: CallAudioRoute; audio: HTMLAudioElement } =>
      Boolean(item.audio)
    );
  if (!playableRoutes.length) return;

  let blocked = false;
  await Promise.all(
    playableRoutes.map(async ({ route, audio }) => {
      audio.muted = false;
      try {
        await audio.play?.();
      } catch (error) {
        blocked = true;
        markAudioPlaybackBlocked(route.id, error);
      }
    })
  );
  if (!blocked) {
    emit({ audioBlocked: false });
  }
};

export function useTelnyxWebRtcCall() {
  const snapshot = React.useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const connect = React.useCallback(async () => {
    await ensureClient();
  }, []);
  const prepareMedia = React.useCallback(async () => {
    await requestMicrophonePermission();
    await ensureClient();
    // Block until the SDK is genuinely registered, so the backend never dials a
    // credential whose socket is not yet able to receive the invite (SIP 486).
    await waitForClientRegistered();
  }, []);
  const waitForIncomingCall = React.useCallback(async (options?: { timeoutMs?: number }) => {
    await ensureClient();
    await waitForClientRegistered();
    return waitForIncomingSdkCall(options);
  }, []);
  const getLineByLogicalId = React.useCallback(
    (logicalCallId: string): BrowserCallLine | null =>
      snapshot.callLines.find(line => line.logicalCallId === logicalCallId) ?? null,
    [snapshot.callLines]
  );

  React.useEffect(() => {
    void updateDevices();
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.addEventListener) {
      return () => undefined;
    }
    const handleDeviceChange = () => {
      void updateDevices();
    };
    navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);
    return () => {
      navigator.mediaDevices.removeEventListener?.('devicechange', handleDeviceChange);
    };
  }, []);

  return {
    ...snapshot,
    connect,
    prepareMedia,
    waitForIncomingCall,
    retryAudioPlayback: retryRemoteAudioPlayback,
    getLineByLogicalId,
    waitForCallProviderIds: async ({
      lineId,
      timeoutMs = 10000,
    }: {
      lineId?: string | null;
      timeoutMs?: number;
    }) => {
      const line = findCallLine(lineId);
      if (!line) {
        return {
          telnyxCallId: null,
          telnyxCallControlId: null,
          telnyxCallSessionId: null,
          telnyxCallLegId: null,
        };
      }
      return waitForOutboundCallControlId(line.call, { timeoutMs });
    },
    // Associate the backend call-log id with an already-created line (outbound
    // calls only learn their logical id after `recordWebRtcCall`). This keeps
    // the leg-ended event's `logicalCallId` populated so auto-end works.
    setLineLogicalId: ({
      sdkCallId,
      logicalCallId,
    }: {
      sdkCallId: string | null;
      logicalCallId: string;
    }) => {
      if (!sdkCallId) return;
      const line =
        callLines.get(sdkCallId) ??
        [...callLines.values()].find(candidate => candidate.call.id === sdkCallId);
      if (!line || line.logicalCallId === logicalCallId) return;
      line.logicalCallId = logicalCallId;
      syncCallLines();
    },
    startOutboundCall: async ({ destinationNumber }: { destinationNumber: string }) => {
      await prepareMedia();
      const rtcClient = await ensureClient();
      await holdCurrentLiveLine();
      const remoteElement = ensureSdkRemoteAudioElement();
      if (remoteElement) {
        rtcClient.remoteElement = remoteElement;
      }
      const call = rtcClient.newCall({
        destinationNumber,
        ...(callerNumber ? { callerNumber } : {}),
        ...(remoteElement ? { remoteElement } : {}),
        audio: true,
      });
      rememberCallLine(call, { preferLive: true, status: 'requesting' });
      emit({ errorMessage: null });
      await applyStoredDevicePreferences(call);
      return waitForOutboundCallControlId(call);
    },
    answerIncomingCall: async (options?: { logicalCallId?: string | null }) => {
      await requestMicrophonePermission();
      await ensureClient();
      const incomingCall = findAnswerableIncomingCall();
      if (!incomingCall?.answer) {
        throw createIncomingCallUnavailableError('before answer');
      }
      const line = rememberCallLine(incomingCall, {
        logicalCallId: options?.logicalCallId,
        status: mapCallState(incomingCall.state),
      });
      await holdCurrentLiveLine(line?.id ?? null);
      await applyStoredDevicePreferences(incomingCall);
      await incomingCall.answer();
      rememberCallLine(incomingCall, {
        logicalCallId: options?.logicalCallId,
        preferLive: true,
        status: 'active',
      });
      emit({ errorMessage: null });
    },
    hangup: async (lineId?: string | null) => {
      const line = findCallLine(lineId) ?? (liveCallLineId ? callLines.get(liveCallLineId) : null);
      const call = line?.call ?? activeCall;
      if (!call?.hangup) return;
      const logicalCallId = line?.logicalCallId ?? null;
      const sdkCallId = call.id ?? null;
      await call.hangup();
      if (line) {
        callLines.delete(line.id);
        if (liveCallLineId === line.id) {
          liveCallLineId = null;
        }
      }
      syncCallLines();
      dispatchCallEnded({ logicalCallId, sdkCallId, lineId: line?.id ?? null });
    },
    holdCurrentCall: async (lineId?: string | null) => {
      if (lineId) {
        await holdLine(lineId);
        return;
      }
      await holdCurrentLiveLine();
    },
    resumeCallLine: async (lineId?: string | null) => {
      await resumeLine(lineId);
    },
    mute: async (lineId?: string | null) => {
      const line = findCallLine(lineId) ?? (liveCallLineId ? callLines.get(liveCallLineId) : null);
      const call = line?.call ?? activeCall;
      if (!call) return;
      if (call.muteAudio) await call.muteAudio();
      else if (call.toggleAudioMute) await call.toggleAudioMute();
      if (line) line.isMuted = true;
      syncCallLines();
      emit({ isMuted: true });
    },
    unmute: async (lineId?: string | null) => {
      const line = findCallLine(lineId) ?? (liveCallLineId ? callLines.get(liveCallLineId) : null);
      const call = line?.call ?? activeCall;
      if (!call) return;
      if (call.unmuteAudio) await call.unmuteAudio();
      else if (call.toggleAudioMute) await call.toggleAudioMute();
      if (line) line.isMuted = false;
      syncCallLines();
      emit({ isMuted: false });
    },
    setMicrophone: async (deviceId: string) => {
      const resolvedDeviceId = resolveSelectedDeviceId(state.microphones, deviceId);
      if (deviceId) window.localStorage.setItem(preferredMicKey, deviceId);
      else window.localStorage.removeItem(preferredMicKey);
      emit({ selectedMicrophoneId: resolvedDeviceId });
      const call = activeCall;
      if (resolvedDeviceId && call?.setAudioInDevice) {
        const setAudioInDevice = call.setAudioInDevice;
        await applyCallDevicePreference(call, 'audioinput', () =>
          setAudioInDevice(resolvedDeviceId)
        );
      }
    },
    setSpeaker: async (deviceId: string) => {
      const resolvedDeviceId = resolveSelectedDeviceId(state.speakers, deviceId);
      if (deviceId) window.localStorage.setItem(preferredSpeakerKey, deviceId);
      else window.localStorage.removeItem(preferredSpeakerKey);
      emit({ selectedSpeakerId: resolvedDeviceId });
      const call = activeCall;
      if (resolvedDeviceId && call?.setAudioOutDevice) {
        const setAudioOutDevice = call.setAudioOutDevice;
        await applyCallDevicePreference(call, 'audiooutput', () =>
          setAudioOutDevice(resolvedDeviceId)
        );
      }
    },
  };
}
